import {
  FIXED_STEP,
  MAX_FRAME_TIME,
  PHYSICS,
  VISUALS,
  WORLD_HEIGHT,
  WORLD_WIDTH,
} from '../config.ts';
import { AudioEngine } from '../audio/Audio.ts';
import { LEVELS, levelByIndex } from '../levels/index.ts';
import type { LevelDefinition } from '../levels/Level.ts';
import type { Vec2 } from '../math/vec2.ts';
import { BackgroundRenderer } from '../rendering/BackgroundRenderer.ts';
import { BallRenderer } from '../rendering/BallRenderer.ts';
import { EffectsRenderer } from '../rendering/EffectsRenderer.ts';
import { FerrofluidRenderer } from '../rendering/FerrofluidRenderer.ts';
import { LevelRenderer } from '../rendering/LevelRenderer.ts';
import { MagnetRenderer } from '../rendering/MagnetRenderer.ts';
import type { PostQuality } from '../rendering/PostProcess.ts';
import { Renderer } from '../rendering/Renderer.ts';
import type { Magnet, MagnetSize } from '../simulation/Magnet.ts';
import { World } from '../simulation/World.ts';
import { Controls } from '../ui/Controls.ts';
import { HUD } from '../ui/HUD.ts';
import { Input } from './Input.ts';

export interface GameOptions {
  levelIndex: number;
  debug: boolean;
  /** Show the S/M/L picker. Off by default — see Controls.setStrengthPickerVisible. */
  strengthPicker: boolean;
  /** Post-processing tier. */
  quality: PostQuality;
}

/** Read the handful of query flags the prototype supports. */
export function readOptions(search: string = location.search): GameOptions {
  const params = new URLSearchParams(search);

  const requested = params.get('fx');
  const quality: PostQuality =
    requested === 'off' || requested === 'low' || requested === 'high' ? requested : 'high';

  return {
    levelIndex: Number(params.get('level') ?? 1) || 1,
    debug: params.get('debug') === '1',
    strengthPicker: params.get('strength') === '1',
    quality,
  };
}

/** Accent colours, shared with the renderers so effects match what they come out of. */
const POSITIVE_COLOR = 0xff5f72;
const NEGATIVE_COLOR = 0x55b4ff;
/**
 * The rectangle the player can actually reach, from the level's own geometry.
 *
 * Derived rather than authored, so a new level cannot forget it — but padded generously,
 * because it only attenuates the field and a magnet dropped just outside the walls is a
 * legal move whose near field still has to read.
 */
function arenaBounds(level: LevelDefinition): [number, number, number, number] {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;

  const grow = (x: number, y: number, r: number) => {
    x0 = Math.min(x0, x - r);
    y0 = Math.min(y0, y - r);
    x1 = Math.max(x1, x + r);
    y1 = Math.max(y1, y + r);
  };

  for (const wall of level.walls) {
    switch (wall.type) {
      case 'segment': {
        const half = wall.thickness / 2;
        grow(wall.x1, wall.y1, half);
        grow(wall.x2, wall.y2, half);
        break;
      }
      case 'circle':
        grow(wall.x, wall.y, wall.r);
        break;
      case 'rect':
        grow(wall.x, wall.y, Math.max(wall.w, wall.h) / 2);
        break;
    }
  }

  if (!Number.isFinite(x0)) return [0, 0, WORLD_WIDTH, WORLD_HEIGHT];

  const pad = 40;
  return [x0 - pad, y0 - pad, x1 + pad, y1 + pad];
}

const GOAL_COLOR = 0x3ad46f;
const IMPACT_COLOR = 0x9fb4dd;

/** Camera shake, in world units at full trauma. */
const MAX_SHAKE = 26;

/** How long the celebration plays before the results card covers it. */
const RESULTS_DELAY = 1.0;

/**
 * The loop.
 *
 * Simulation runs at a fixed 60 Hz through an accumulator; rendering runs at whatever
 * rate the display offers and interpolates between the last two simulation states. Device
 * refresh rate therefore changes how smooth the game looks and nothing about how it
 * behaves.
 *
 * This is also where feedback is assembled. The simulation raises events and knows
 * nothing about who is listening; everything that turns those into sound, light, shake
 * and vibration happens here, so none of it can leak back into the physics.
 */
export class Game {
  readonly renderer = new Renderer();
  readonly audio = new AudioEngine();
  world: World;

  private readonly options: GameOptions;
  private readonly hud: HUD;
  private controls!: Controls;
  private input!: Input;

  private backgroundRenderer!: BackgroundRenderer;
  private levelRenderer!: LevelRenderer;
  private fluidRenderer!: FerrofluidRenderer;
  private ballRenderer!: BallRenderer;
  private magnetRenderer!: MagnetRenderer;
  private effects!: EffectsRenderer;

  private accumulator = 0;
  private lastFrameAt = 0;
  private elapsed = 0;
  private selectedStrength: MagnetSize = 'M';
  private levelIndex: number;

  /**
   * Camera trauma, 0..1. Shake is trauma squared so small knocks stay subtle while a
   * solve really moves the board.
   */
  private trauma = 0;
  /** Eased back to 1 after the brief slow-motion on a solve. */
  private timeScale = 1;
  /**
   * Seconds left before the results card appears. The celebration is the payoff for
   * solving the level, and a modal that lands on the same frame as the win hides it.
   */
  private resultsDelay = 0;
  /** What the outcome card will say once resultsDelay expires. */
  private outcome: 'solved' | 'failed' | null = null;
  /** Eased 1 -> 0 as a run starts, fading out the planning contours. */
  private contour = 1;

  // Debug sampling.
  private fps = 0;
  private physicsMs = 0;
  private renderMs = 0;
  private stepsPerFrame = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    options: GameOptions = readOptions(),
  ) {
    this.options = options;
    this.levelIndex = Math.min(Math.max(options.levelIndex, 1), LEVELS.length);
    this.hud = new HUD();
    this.world = new World(levelByIndex(this.levelIndex));
  }

  async start(): Promise<void> {
    await this.renderer.init(this.canvas, this.options.quality);
    this.renderer.setPostQuality(this.options.quality);

    this.backgroundRenderer = new BackgroundRenderer();
    this.levelRenderer = new LevelRenderer();
    this.ballRenderer = new BallRenderer();
    this.magnetRenderer = new MagnetRenderer();
    this.effects = new EffectsRenderer();
    this.fluidRenderer = new FerrofluidRenderer(
      this.world.fluid.capacity,
      pickTextureScale(),
      this.renderer.floatTargets.halfFloat,
    );

    this.renderer.layers.background.addChild(this.backgroundRenderer.mesh);
    this.renderer.layers.level.addChild(this.levelRenderer.container);
    this.renderer.layers.fluid.addChild(this.fluidRenderer.mesh);
    this.renderer.layers.ball.addChild(this.ballRenderer.container);
    this.renderer.layers.magnets.addChild(this.magnetRenderer.container);
    this.renderer.layers.effects.addChild(this.effects.container);

    this.renderer.onResize((view) => this.backgroundRenderer.setView(view));
    this.levelRenderer.build(this.world.level);
    this.backgroundRenderer.setArena(...arenaBounds(this.world.level));

    this.controls = new Controls({
      onRun: () => {
        this.audio.uiClick();
        if (this.world.isPlanning) this.startRun();
        else this.adjust();
      },
      onAdjust: () => {
        this.audio.uiClick();
        this.adjust();
      },
      onClear: () => {
        this.audio.uiClick();
        this.clearBoard();
      },
      onNext: () => {
        this.audio.uiClick();
        this.advanceLevel();
      },
      onStrength: (size) => {
        this.audio.uiClick();
        this.selectedStrength = size;
      },
      onToggleMute: () => {
        this.audio.unlock();
        this.audio.setMuted(!this.audio.isMuted);
        this.controls.setMuted(this.audio.isMuted);
        if (!this.audio.isMuted) this.audio.uiClick();
      },
    });
    this.controls.setStrengthPickerVisible(this.options.strengthPicker);
    this.controls.setMuted(this.audio.isMuted);

    this.input = new Input(this.canvas, this.renderer, {
      // Browsers will not start audio without a gesture; this is the first one we get.
      onGesture: () => this.audio.unlock(),
      hitTest: (point) => this.world.magnetAt(point.x, point.y),
      onTapEmpty: (point) => this.placeMagnet(point),
      onTapMagnet: (magnet) => this.flipMagnet(magnet),
      onDragStart: () => {},
      onDrag: (magnet, point) => this.world.moveMagnet(magnet, point.x, point.y),
      onDragEnd: () => {},
      onLongPress: (magnet) => this.removeMagnet(magnet),
    });

    this.hud.setDebugEnabled(this.options.debug);
    if (this.options.debug) this.renderer.enableDrawCallCounting();
    this.presentLevel();
    this.attachResize();

    this.lastFrameAt = performance.now();
    this.renderer.app.ticker.add(() => this.frame());
  }

  // --- actions -------------------------------------------------------------

  private placeMagnet(point: Vec2): void {
    if (!this.world.isPlanning) {
      this.hud.showHint('Stop the run to move magnets.', 2);
      this.audio.uiClick();
      return;
    }

    const magnet = this.world.addMagnet(point.x, point.y, this.selectedStrength);
    if (!magnet) {
      this.hud.showHint(
        this.world.level.magnetsAvailable === 0
          ? 'No magnets in this level.'
          : 'No magnets left — hold one to remove it.',
        2.5,
      );
      this.audio.uiClick();
      return;
    }

    this.hud.hideIntro();
    this.audio.magnetPlace();
    this.effects.spawn('ring', magnet.position.x, magnet.position.y, magnet.radius * 5, colorFor(magnet), 0.5, 0.13, 2.4);
    this.effects.spawn('burst', magnet.position.x, magnet.position.y, magnet.radius * 2.4, colorFor(magnet), 0.35, 0, 1.6);
    this.trauma = Math.min(1, this.trauma + 0.16);
    vibrate(12);
    this.syncUi();
  }

  private flipMagnet(magnet: Magnet): void {
    magnet.flip();
    this.audio.magnetFlip(magnet.polarity);
    // A shockwave in the new polarity's colour: the read is "this changed", not "this is".
    this.effects.spawn('ring', magnet.position.x, magnet.position.y, magnet.radius * 6.5, colorFor(magnet), 0.45, 0.09, 2.6);
    this.trauma = Math.min(1, this.trauma + 0.12);
    vibrate(8);
  }

  private removeMagnet(magnet: Magnet): void {
    const color = colorFor(magnet);
    const { x, y } = magnet.position;
    this.world.removeMagnet(magnet);
    this.audio.magnetRemove();
    this.effects.spawn('burst', x, y, magnet.radius * 2.8, color, 0.4, 0, 1.8);
    vibrate([8, 24, 8]);
    this.syncUi();
  }

  /** Commit the layout and let it play out. */
  private startRun(): void {
    if (!this.world.isPlanning) return;
    this.hud.hideIntro();
    this.world.start();
    this.hud.setOutcome(null);
    this.audio.runStart();
    this.renderer.post.addFlash(0.05, 0.09, 0.13);
    this.trauma = Math.min(1, this.trauma + 0.2);
    vibrate(14);
    this.syncUi();
  }

  /** Back to planning, magnets left where they are. The iteration loop. */
  private adjust(): void {
    this.world.rewind();
    this.effects.clear();
    this.trauma = 0;
    this.timeScale = 1;
    this.resultsDelay = 0;
    this.hud.setOutcome(null);
    this.syncUi();
  }

  /** Back to planning with the board cleared. */
  private clearBoard(): void {
    this.world.reset();
    this.magnetRenderer.clear();
    this.effects.clear();
    this.trauma = 0;
    this.timeScale = 1;
    this.resultsDelay = 0;
    this.hud.setOutcome(null);
    this.presentLevel();
    this.syncUi();
  }

  private advanceLevel(): void {
    if (this.levelIndex >= LEVELS.length) {
      this.clearBoard();
      return;
    }
    this.levelIndex++;
    this.loadLevel(levelByIndex(this.levelIndex));
  }

  loadLevel(level: LevelDefinition): void {
    this.world.load(level);
    this.magnetRenderer.clear();
    this.effects.clear();
    this.trauma = 0;
    this.timeScale = 1;
    this.resultsDelay = 0;
    this.hud.setOutcome(null);
    this.levelRenderer.build(level);
    this.backgroundRenderer.setArena(...arenaBounds(level));
    this.presentLevel();
    this.syncUi();
  }

  /** Title card, level announcement, and the next-level button's availability. */
  private presentLevel(): void {
    const level = this.world.level;
    this.hud.showIntro(this.levelIndex, level.name, level.hint);
    this.controls.setHasNextLevel(this.levelIndex < LEVELS.length);
    this.audio.levelStart();
  }

  private onSolved(x: number, y: number): void {
    this.hud.setResults(this.world.elapsed, this.world.magnetsUsed, this.world.goalProgress);
    this.audio.goal();

    // Three rings staggered outwards, a flash, a shove to the camera, and a beat of slow
    // motion. Individually none of these is much; together they are the difference
    // between the level ending and the level being won.
    this.effects.spawn('burst', x, y, 210, GOAL_COLOR, 0.7, 0, 2.4);
    this.effects.spawn('ring', x, y, 280, GOAL_COLOR, 0.7, 0.12, 3.0);
    this.effects.spawn('ring', x, y, 460, GOAL_COLOR, 1.0, 0.07, 2.2);
    this.renderer.post.addFlash(0.1, 0.22, 0.2);
    this.trauma = Math.min(1, this.trauma + 0.55);
    this.timeScale = 0.32;
    this.outcome = 'solved';
    this.resultsDelay = RESULTS_DELAY;
    vibrate([18, 40, 26]);
  }

  /**
   * A run that did not get there. Deliberately understated: the player is one tap from
   * adjusting a magnet and going again, and punishing that with noise would make
   * iterating feel like losing.
   */
  private onFailed(): void {
    this.hud.setResults(this.world.elapsed, this.world.magnetsUsed, this.world.goalProgress);
    this.audio.runFailed();
    this.outcome = 'failed';
    this.resultsDelay = RESULTS_DELAY * 0.6;
    vibrate(20);
  }

  private syncUi(): void {
    this.hud.setMagnets(this.world.magnetsRemaining, this.world.level.magnetsAvailable);
    this.hud.setGoalProgress(this.world.goalProgress);
    // Nothing to run until at least one magnet is down: an empty field moves nothing, and
    // letting the player watch that for twenty-six seconds is not a lesson.
    this.controls.setPhase(this.world.phase, this.world.field.magnets.length > 0);
  }

  private attachResize(): void {
    const resize = () => {
      const app = document.getElementById('app');
      const width = app?.clientWidth || window.innerWidth;
      const height = app?.clientHeight || window.innerHeight;
      this.renderer.resize(width, height);
    };
    window.addEventListener('resize', resize);
    window.addEventListener('orientationchange', resize);
    // Safari changes the visual viewport without firing a window resize when the URL bar
    // collapses; dvh plus this keeps the canvas honest.
    window.visualViewport?.addEventListener('resize', resize);
    resize();
  }

  // --- frame ---------------------------------------------------------------

  private frame(): void {
    const now = performance.now();
    let frameSeconds = (now - this.lastFrameAt) / 1000;
    this.lastFrameAt = now;
    if (!Number.isFinite(frameSeconds) || frameSeconds < 0) frameSeconds = 0;
    // Cap the catch-up so a backgrounded tab does not return and simulate a minute.
    frameSeconds = Math.min(frameSeconds, MAX_FRAME_TIME);

    this.fps =
      this.fps === 0
        ? 1 / Math.max(frameSeconds, 1e-4)
        : this.fps * 0.92 + (1 / Math.max(frameSeconds, 1e-4)) * 0.08;

    this.input.update(now);

    // Slow motion eases back on real time, not scaled time, or it would never recover.
    this.timeScale += (1 - this.timeScale) * (1 - Math.exp(-frameSeconds * 3.2));
    this.trauma = Math.max(0, this.trauma - frameSeconds * 1.7);

    const physicsStart = performance.now();
    this.accumulator += frameSeconds * this.timeScale;
    let steps = 0;
    while (this.accumulator >= FIXED_STEP) {
      this.world.step(FIXED_STEP);
      this.accumulator -= FIXED_STEP;
      steps++;
      // Hard ceiling: better to run slow than to stall entirely.
      if (steps >= 5) {
        this.accumulator = 0;
        break;
      }
    }
    this.stepsPerFrame = steps;
    this.physicsMs = this.physicsMs * 0.9 + (performance.now() - physicsStart) * 0.1;

    this.drainEvents();

    const alpha = this.accumulator / FIXED_STEP;
    this.elapsed += frameSeconds;

    const renderStart = performance.now();
    if (this.options.debug) this.renderer.resetDrawCalls();
    this.renderer.post.update(frameSeconds);
    this.applyShake();

    // Contours belong to planning. They ease out rather than snapping, so committing a
    // run reads as the plan handing over to the simulation.
    const contourTarget = this.world.isPlanning ? 1 : 0;
    this.contour += (contourTarget - this.contour) * (1 - Math.exp(-frameSeconds * 5));
    this.backgroundRenderer.update(this.world.field, this.elapsed, this.contour);
    this.levelRenderer.update(this.elapsed, this.world.goalProgress, this.world.solved);
    this.fluidRenderer.updateGeometry(this.world.fluid, alpha);
    this.fluidRenderer.update(this.world.field, this.world.fluid, frameSeconds);
    this.ballRenderer.update(this.world.ball, alpha);
    this.magnetRenderer.update(
      this.world.field.magnets,
      {
        magnet: this.input.magnet,
        dragging: this.input.dragging,
        holdProgress: this.input.holdProgress,
      },
      frameSeconds,
    );
    this.effects.update(frameSeconds);

    this.fluidRenderer.renderDensity(this.renderer.app.renderer);
    this.renderer.render();
    this.renderMs = this.renderMs * 0.9 + (performance.now() - renderStart) * 0.1;

    this.audio.update(
      frameSeconds,
      this.world.isRunning ? this.world.fluid.meanSpeed : 0,
      this.world.field.magnets.length,
    );

    this.hud.tickHint(frameSeconds);
    this.syncUi();

    if (this.resultsDelay > 0) {
      this.resultsDelay -= frameSeconds;
      if (this.resultsDelay <= 0 && this.outcome) {
        this.hud.setOutcome(this.outcome, this.outcome === 'failed' ? this.failureNote() : '');
      }
    }
    if (this.options.debug) {
      this.hud.setDebug(
        {
          fps: this.fps,
          particles: this.world.fluid.count,
          magnets: this.world.field.magnets.length,
          physicsMs: this.physicsMs,
          renderMs: this.renderMs,
          stepsPerFrame: this.stepsPerFrame,
          resolution: this.renderer.app.renderer.resolution,
          drawCalls: this.renderer.drawCalls,
        },
        now,
      );
    }
  }

  /** Turn simulation events into sound, light and vibration. */
  private drainEvents(): void {
    const events = this.world.events;
    for (const event of events) {
      switch (event.type) {
        case 'ballImpact': {
          const strength = Math.min(1, event.speed / 900);
          this.audio.ballImpact(event.speed);
          this.effects.spawn(
            'ring',
            event.x,
            event.y,
            (this.world.ball?.radius ?? 40) * (1.6 + strength * 2.4),
            IMPACT_COLOR,
            0.3 + strength * 0.2,
            0.22,
          );
          this.trauma = Math.min(1, this.trauma + strength * 0.3);
          if (strength > 0.35) vibrate(Math.round(6 + strength * 14));
          break;
        }
        case 'started':
          break;
        case 'solved':
          this.onSolved(event.x, event.y);
          break;
        case 'failed':
          this.onFailed();
          break;
      }
    }
    events.length = 0;
  }

  /** A line of diagnosis on the failure card, from how close the run actually got. */
  private failureNote(): string {
    const progress = this.world.goalProgress;
    if (progress <= 0.02) return 'Nothing reached the goal. Try a magnet closer to it.';
    if (progress < 0.5) return 'Some of it arrived. The route is right, the pull is not.';
    return 'Very close. A little more had to make it in.';
  }

  /**
   * Two out-of-phase sine pairs rather than random offsets: noise-driven shake reads as a
   * dropped frame, a smooth wobble reads as impact.
   */
  private applyShake(): void {
    if (this.trauma <= 0.001) {
      this.renderer.setShake(0, 0);
      return;
    }
    const amount = this.trauma * this.trauma * MAX_SHAKE;
    const t = this.elapsed;
    this.renderer.setShake(
      Math.sin(t * 47.3) * amount,
      Math.sin(t * 39.7 + 1.9) * amount * 0.85,
    );
  }
}

function colorFor(magnet: Magnet): number {
  return magnet.polarity === 1 ? POSITIVE_COLOR : NEGATIVE_COLOR;
}

/** Android honours this; iOS Safari ignores it. Either way it must never throw. */
function vibrate(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Some browsers reject vibration outside a user gesture. Not worth reporting.
  }
}

/** Phones get a smaller density texture; it is the one cost that scales with the screen. */
function pickTextureScale(): number {
  const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  return coarsePointer ? VISUALS.fluidTextureScaleMobile : VISUALS.fluidTextureScale;
}

/** Exposed for live tuning from the console: __magneto.PHYSICS.cohesion = 8000, etc. */
export const TUNABLES = { PHYSICS, VISUALS };
